/**
 * /admin — onglet « Véhicules à vendre » (2026-09-16).
 *
 * Le propriétaire qualifie CHAQUE dépôt à la main : c'est ce qui protège le
 * réseau de garages. Un dépôt n'est transmis qu'une fois validé, et la
 * transmission elle-même reste manuelle (aucun accès garage n'existe).
 *
 * Les coordonnées du vendeur ne vivent QUE dans cet écran : l'alerte Telegram
 * ne les porte pas.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { nombre } from '../components/Affaires/format';
import api from '../services/api';
import AdminHistorique from './AdminHistorique';
import './AdminVente.css';

const LIBELLES = {
  nouveau: 'Nouveau', valide: 'Validé', publie: 'Publié aux garages', envoye: 'Envoyé aux garages',
  cloture: 'Clôturé', vendu: 'Vendu', refuse: 'Refusé',
};
const SUITES = {
  nouveau: [['valide', 'Valider'], ['refuse', 'Refuser']],
  // « Publier » rend la fiche ANONYME visible des garages du secteur pendant
  // 30 jours ; « Marquer envoyé » reste la voie manuelle d'avant le réseau.
  valide: [['publie', 'Publier aux garages'], ['envoye', 'Marquer envoyé'], ['refuse', 'Refuser']],
  publie: [['vendu', 'Marquer vendu'], ['cloture', 'Clôturer'], ['refuse', 'Refuser']],
  envoye: [['vendu', 'Marquer vendu'], ['refuse', 'Refuser']],
  cloture: [['vendu', 'Marquer vendu']],
  vendu: [],
  refuse: [],
};
const NIVEAUX = {
  SOLIDE: 'cote solide', INDICATIVE: 'cote indicative',
  PERCENTILE: 'positionnement', ESTIMATION: 'estimation indicative',
};
const ETATS = { tres_bon: 'Très bon état', bon: 'Bon état', frais: 'Frais à prévoir', hs: 'Ne roule pas' };

const euros = (n) => (n == null ? '—' : `${nombre(n)} €`);
const quand = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');

function Depot({ depot, onStatut, onNote, occupe }) {
  const [refus, setRefus] = useState(false);
  const [motif, setMotif] = useState('');
  const [noter, setNoter] = useState(false);
  const [note, setNote] = useState('');
  const v = depot.vehicule;
  const e = depot.estimation;
  const lecture = depot.lecture_prix_souhaite;

  return (
    <article className="zk-carte zk-adv-depot">
      <header>
        <h3>{v.marque} {v.modele} {v.annee}</h3>
        <span className={`zk-adv-statut ${depot.statut}`}>{LIBELLES[depot.statut] || depot.statut}</span>
      </header>
      <p className="zk-adv-specs">
        {[`${nombre(v.kilometrage)} km`, v.energie, v.boite, ETATS[depot.etat_general]].filter(Boolean).join(' · ')}
        {v.marque_saisie && <em> · saisi « {v.marque_saisie} {v.modele_saisi || v.modele} »</em>}
      </p>

      <dl className="zk-adv-chiffres">
        <div><dt>Particulier</dt><dd>{euros(e.prix_particulier)}<small>{NIVEAUX[e.niveau] || 'pas d’estimation'}</small></dd></div>
        <div><dt>Reprise</dt><dd>{euros(e.reprise_bas)} – {euros(e.reprise_haut)}</dd></div>
        <div>
          <dt>Prix souhaité</dt>
          <dd className={lecture && lecture.ecart_pct > 20 ? 'attention' : undefined}>
            {euros(depot.prix_souhaite)}
            {lecture && <small>{lecture.ecart_pct > 0 ? '+' : ''}{lecture.ecart_pct} % · {lecture.position}</small>}
          </dd>
        </div>
      </dl>

      <p className="zk-adv-contact">
        <b>{depot.prenom}</b> · {depot.code_postal} ·{' '}
        <a href={`tel:${depot.telephone}`}>{depot.telephone}</a> ·{' '}
        <a href={`mailto:${depot.email}`}>{depot.email}</a>
      </p>
      <p className="zk-adv-date">Déposé le {quand(depot.created_at)}{depot.statut_at ? ` · ${LIBELLES[depot.statut]} le ${quand(depot.statut_at)}` : ''}</p>
      {depot.motif_refus && <p className="zk-adv-motif">Motif du refus : {depot.motif_refus}</p>}
      {depot.note && <pre className="zk-adv-note">{depot.note}</pre>}
      <AdminHistorique dossier={depot} libelles={LIBELLES} />

      {depot.publie_le && (
        <p className={`zk-adv-publication${depot.expire ? ' expire' : ''}`}>
          {depot.expire
            ? `Publication expirée le ${quand(depot.expire_le)} — la fiche n’est plus visible des garages`
            : `Visible des garages ${depot.zone_libelle ? `du secteur ${depot.zone_libelle} ` : ''}jusqu’au ${quand(depot.expire_le)}`}
        </p>
      )}

      {depot.deverrouillages?.length > 0 && (
        <div className="zk-adv-deverrouillages">
          <b>{depot.deverrouillages.length} garage{depot.deverrouillages.length > 1 ? 's ont' : ' a'} demandé les coordonnées</b>
          <ul>
            {depot.deverrouillages.map((d) => (
              <li key={`${d.garage_id}-${d.cree_le}`}>
                {d.nom} <small>{d.email} · {quand(d.cree_le)}</small>
              </li>
            ))}
          </ul>
        </div>
      )}

      {depot.photos?.length > 0 && (
        <div className="zk-adv-photos">
          {depot.photos.map((nom, i) => (
            <a key={nom} href={`/api/admin/vente/${depot.id}/photos/${nom}`} target="_blank" rel="noreferrer">
              <img src={`/api/admin/vente/${depot.id}/photos/${nom}`}
                alt={`${v.marque} ${v.modele} du dépôt ${depot.id}, vue ${i + 1}`} loading="lazy" />
            </a>
          ))}
        </div>
      )}

      {!refus && !noter && (
        <div className="zk-adv-actions">
          {(SUITES[depot.statut] || []).map(([statut, libelle]) => (
            <button
              key={statut} type="button" disabled={occupe}
              className={`zk-adm-btn${statut === 'refuse' ? ' rouge' : statut === 'vendu' ? ' vert' : ' bleu'}`}
              onClick={() => (statut === 'refuse' ? setRefus(true) : onStatut(depot.id, statut))}
            >
              {libelle}
            </button>
          ))}
          {/* Noter ne change pas le statut : un dépôt vendu ou refusé peut
              encore recevoir un rappel du vendeur. */}
          <button type="button" className="zk-adm-btn" disabled={occupe} onClick={() => setNoter(true)}>
            Noter un échange
          </button>
        </div>
      )}

      {noter && (
        <form
          className="zk-adv-refus"
          onSubmit={(ev) => {
            ev.preventDefault();
            onNote(depot.id, note.trim()).then((ok) => { if (ok) { setNoter(false); setNote(''); } });
          }}
        >
          <label htmlFor={`note-${depot.id}`}>Que s’est-il passé ?</label>
          <input id={`note-${depot.id}`} required maxLength={1000} value={note} onChange={(ev) => setNote(ev.target.value)}
            placeholder="Appelé, il accepte 8 500 € — à rappeler lundi" />
          <div className="zk-adv-actions">
            <button type="submit" className="zk-adm-btn bleu" disabled={occupe || !note.trim()}>Enregistrer</button>
            <button type="button" className="zk-adm-btn" onClick={() => { setNoter(false); setNote(''); }}>Annuler</button>
          </div>
        </form>
      )}

      {refus && (
        <form
          className="zk-adv-refus"
          onSubmit={(ev) => { ev.preventDefault(); onStatut(depot.id, 'refuse', motif).then(() => setRefus(false)); }}
        >
          <label htmlFor={`motif-${depot.id}`}>Motif du refus</label>
          <input id={`motif-${depot.id}`} required maxLength={500} value={motif} onChange={(ev) => setMotif(ev.target.value)}
            placeholder="Hors zone, véhicule trop ancien, injoignable…" />
          <div className="zk-adv-actions">
            <button type="submit" className="zk-adm-btn rouge" disabled={occupe || !motif.trim()}>Confirmer le refus</button>
            <button type="button" className="zk-adm-btn" onClick={() => setRefus(false)}>Annuler</button>
          </div>
        </form>
      )}
    </article>
  );
}

export default function AdminVente({ signaler, onChange }) {
  const [depots, setDepots] = useState([]);
  const [comptes, setComptes] = useState({});
  const [filtre, setFiltre] = useState('');
  const [chargement, setChargement] = useState(true);
  const [occupe, setOccupe] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await api.get('/api/admin/vente', { params: filtre ? { statut: filtre } : {} });
      setDepots(data?.depots || []);
      setComptes(data?.comptes || {});
    } catch {
      signaler?.('Erreur de chargement des véhicules à vendre', false);
    }
    setChargement(false);
  }, [filtre, signaler]);

  useEffect(() => { charger(); }, [charger]);

  async function changerStatut(id, statut, motif) {
    setOccupe(true);
    try {
      const { data } = await api.post(`/api/admin/vente/${id}/statut`, { statut, motif: motif || null });
      setDepots((ds) => ds.map((d) => (d.id === id ? data : d)));
      signaler?.(`Dépôt #${id} : ${LIBELLES[statut]}`);
      charger();
      onChange?.();
    } catch (err) {
      signaler?.(err.response?.data?.detail?.message || 'Changement de statut refusé', false);
    } finally {
      setOccupe(false);
    }
  }

  async function ajouterNote(id, note) {
    setOccupe(true);
    try {
      const { data } = await api.post(`/api/admin/vente/${id}/note`, { note });
      setDepots((ds) => ds.map((d) => (d.id === id ? data : d)));
      signaler?.(`Dépôt #${id} : échange noté`);
      return true;
    } catch (err) {
      signaler?.(err.response?.data?.detail?.message || 'Note refusée', false);
      return false;
    } finally {
      setOccupe(false);
    }
  }

  const total = Object.values(comptes).reduce((a, b) => a + b, 0);

  return (
    <div>
      <div className="zk-adv-tete">
        <div className="zk-adm-onglets" role="group" aria-label="Filtrer par statut">
          <button type="button" className={`zk-aff-pilule${filtre === '' ? ' on' : ''}`} onClick={() => setFiltre('')}>
            Tous · {total}
          </button>
          {Object.keys(LIBELLES).map((s) => (
            <button key={s} type="button" className={`zk-aff-pilule${filtre === s ? ' on' : ''}`} onClick={() => setFiltre(s)}>
              {LIBELLES[s]} · {comptes[s] || 0}
            </button>
          ))}
        </div>
        <a className="zk-adm-btn" href={`/api/admin/vente/export.csv${filtre ? `?statut=${filtre}` : ''}`}>Exporter en CSV</a>
      </div>

      {chargement ? (
        <p className="zk-adm-vide">Chargement…</p>
      ) : depots.length === 0 ? (
        <p className="zk-adm-vide">Aucun véhicule à vendre {filtre ? `au statut « ${LIBELLES[filtre]} »` : 'pour l’instant'}.</p>
      ) : (
        <div className="zk-adv-liste">
          {depots.map((d) => <Depot key={d.id} depot={d} onStatut={changerStatut} onNote={ajouterNote} occupe={occupe} />)}
        </div>
      )}
    </div>
  );
}
